import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { raffleAPI } from '@/lib/api';

/**
 * Simple search + add/remove list of agents (by NPN) allowed to run
 * Live Capture for an event. Reuses the shared assignable-agents list.
 */
export default function LiveCaptureAgentPicker({ value = [], onChange }) {
  const [agents, setAgents] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    raffleAPI.assignableAgents()
      .then((r) => setAgents(r.data.agents || []))
      .catch(() => {});
  }, []);

  const nameByNpn = useMemo(() => {
    const m = {};
    agents.forEach((a) => { m[a.npn] = a.name; });
    return m;
  }, [agents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents.slice(0, 50);
    return agents.filter(
      (a) => (a.name || '').toLowerCase().includes(q) || (a.npn || '').includes(q),
    ).slice(0, 50);
  }, [agents, query]);

  const toggle = (npn) => {
    const next = value.includes(npn) ? value.filter((n) => n !== npn) : [...value, npn];
    onChange(next);
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="live-capture-agents">
      <p className="text-xs font-medium text-slate-700 mb-1">Live Capture Agents</p>
      <p className="text-[11px] text-slate-500 mb-2">
        Search and add the agents who should see this event in the Live Capture kiosk.
        They are also added as participants of the event.
      </p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="live-capture-selected">
          {value.map((npn) => (
            <Badge
              key={npn}
              variant="secondary"
              className="flex items-center gap-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            >
              {nameByNpn[npn] || `#${npn}`}
              <button
                type="button"
                onClick={() => toggle(npn)}
                className="ml-0.5 hover:text-emerald-950"
                data-testid={`live-capture-remove-${npn}`}
                aria-label={`Remove ${nameByNpn[npn] || npn}`}
              >
                <X size={11} />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agents by name or NPN…"
          className="pl-8 h-8 text-sm bg-white"
          data-testid="live-capture-agent-search"
        />
      </div>

      <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
        {filtered.length === 0 && (
          <p className="p-3 text-xs text-slate-400">No agents match.</p>
        )}
        {filtered.map((a) => (
          <label
            key={a.npn}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
            data-testid={`live-capture-agent-${a.npn}`}
          >
            <Checkbox checked={value.includes(a.npn)} onCheckedChange={() => toggle(a.npn)} />
            <span className="truncate">{a.name}</span>
            <span className="text-[11px] text-slate-400 ml-auto">#{a.npn}</span>
          </label>
        ))}
      </div>

      {value.length > 0 && (
        <p className="text-[11px] text-emerald-700 mt-1">
          {value.length} agent{value.length === 1 ? '' : 's'} can run Live Capture
        </p>
      )}
    </div>
  );
}
